import ArrowBack from '@material-ui/icons/ArrowBack';
import { Loading } from '@monorepo/shared/components/Loading';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { IconButton } from '@monorepo/shared/componentsV2/Button/IconButton';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { GenericError } from '@monorepo/shared/componentsV2/errorDisplays/GenericError';
import { fieldValueDisplayFactory } from '@monorepo/shared/componentsV2/fieldDataType/values/fieldValueDisplayFactory';
import { Header } from '@monorepo/shared/componentsV2/Header';
import { Main } from '@monorepo/shared/componentsV2/Main';
import { Table } from '@monorepo/shared/componentsV2/Table/Table';
import { TableColumn } from '@monorepo/shared/componentsV2/Table/types';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { useHasOrgUpdatePermissionOrSuperAdmin } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { useResource } from '@monorepo/shared/hooks/resources/useResource';
import { useResourceTypeWithProperties } from '@monorepo/shared/hooks/resources/useResourceTypeWithProperties';
import { useOpenClose } from '@monorepo/shared/hooks/useOpenClose';
import {
  fillHeightAndScrollable,
  page,
  verticalLayout,
} from '@monorepo/shared/styles/layout';
import { bodySmall, header4 } from '@monorepo/shared/styles/text';
import { FieldResponse, FieldValueVersion } from 'mapistry-shared';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useLinkOnSameOrg } from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameOrg';
import {
  ValueWithHistory,
  ValueWithHistoryColumnHeader,
} from '../ValueWithHistory';
import { EditResourceModal } from '../EditResourceModal';
import { FieldVersionHistoryModal } from './FieldVersionHistoryModal';

const Page = styled(Main)`
  ${verticalLayout}
  ${page}
`;

const TitleContainer = styled.div`
  padding: 1.5rem 1rem 1rem;
`;

const H2 = styled.h2`
  ${header4}
  padding-bottom: 0.5rem;
  margin: 0;
`;

const Subtext = styled.div`
  ${bodySmall}
  color: ${({ theme }) => theme.colors.gray666};
`;

const Content = styled.div`
  ${fillHeightAndScrollable};
`;

const StyledTable = styled(Table)`
  padding-bottom: 4rem;
` as typeof Table;

const NameText = styled.div`
  color: ${({ theme }) => theme.colors.gray333};
`;

type ResourcePropertyRow = {
  id: string;
  name: string;
  displayedValue: React.ReactElement | string;
  field?: FieldResponse;
  versions: FieldValueVersion[];
};

function getColumns(
  onShowHistory: (row: ResourcePropertyRow) => void,
): TableColumn<ResourcePropertyRow>[] {
  return [
    {
      id: 'name',
      header: 'Field',
      contents: (row) => <NameText>{row.name}</NameText>,
    },
    {
      id: 'value',
      header: <ValueWithHistoryColumnHeader label="Value" />,
      contents: (row) => (
        <ValueWithHistory
          displayValue={row.displayedValue}
          fieldName={row.name}
          onShowHistory={() => onShowHistory(row)}
          versions={row.versions}
        />
      ),
    },
  ];
}

function ResourceDetailsContent() {
  const { resourceTypeId, organizationId, resourceId } = useParams();
  const canEditOrgSettings =
    useHasOrgUpdatePermissionOrSuperAdmin(organizationId);
  const navigate = useNavigate();
  const [editModalOpen, openEditModal, closeEditModal] = useOpenClose();
  const [historyRow, setHistoryRow] = useState<ResourcePropertyRow | null>(
    null,
  );

  const { resource, isLoading: isLoadingResource } = useResource({
    organizationId,
    resourceTypeId,
    resourceId,
  });

  const {
    resourceType,
    resourceProperties,
    isLoading: isLoadingResourceType,
  } = useResourceTypeWithProperties({
    organizationId,
    resourceTypeId,
  });

  const backLink = useLinkOnSameOrg(
    `/data-setup/resource-types/${resourceTypeId}/resources`,
  );
  const handleBack = useCallback(
    () => navigate(backLink),
    [navigate, backLink],
  );

  const columns = useMemo(() => getColumns(setHistoryRow), []);

  if (isLoadingResource || isLoadingResourceType) {
    return <Loading />;
  }

  if (!resource || !resourceType || !resourceProperties) {
    return <GenericError />;
  }

  const rows: ResourcePropertyRow[] = [
    ...(resourceType.isSiteSpecific
      ? [
          {
            id: 'associated-site',
            name: 'Site Association',
            displayedValue: resource.project?.name || '',
            versions: [],
          },
        ]
      : []),
    ...resourceProperties.map((prop) => {
      const propertyValue = resource.propertyValues[prop.id];
      return {
        id: prop.id,
        name: prop.name,
        displayedValue: propertyValue?.fieldValue
          ? fieldValueDisplayFactory({
              fieldType: prop.type,
              fieldValue: propertyValue.fieldValue,
            })
          : '',
        field: prop,
        versions: propertyValue?.valueVersions ?? [],
      };
    }),
  ];

  const addResourcesSubtext = canEditOrgSettings
    ? 'You may adjust the values associated with these fields by clicking Edit Resource values.'
    : 'Ask your organization administrator to adjust the values.';

  return (
    <>
      <Page>
        <Header
          variant="bottomBorder"
          title={resource.name || ''}
          leftActions={
            <IconButton onClick={handleBack}>
              <ArrowBack />
              <VisuallyHidden>Back</VisuallyHidden>
            </IconButton>
          }
          rightActions={
            canEditOrgSettings ? (
              <Button
                variant="contained"
                color="primary"
                onClick={openEditModal}
              >
                Edit Resource values
              </Button>
            ) : null
          }
        />
        <Content>
          <TitleContainer>
            <H2>Resource Fields</H2>
            <Subtext>
              These fields are defined on the Resource type and cannot be
              changed at the Resource level. <br />
              {addResourcesSubtext}
            </Subtext>
          </TitleContainer>
          <StyledTable
            ariaLabel={`${resource.name} field values`}
            columns={columns}
            rows={rows}
          />
        </Content>
      </Page>
      {/* this makes sure the modal is created fresh each time its opened & skips performing unnecessary logic */}
      {editModalOpen && (
        <EditResourceModal
          organizationId={organizationId}
          resourceTypeId={resourceTypeId}
          resourceId={resourceId}
          open={editModalOpen}
          onClose={closeEditModal}
        />
      )}
      {historyRow?.field && (
        <FieldVersionHistoryModal
          fieldName={historyRow.field.name}
          fieldType={historyRow.field.type}
          onClose={() => setHistoryRow(null)}
          resourceName={resource.name}
          versions={historyRow.versions}
        />
      )}
    </>
  );
}

export const ResourceDetails = () => (
  <ErrorBoundary>
    <ResourceDetailsContent />
  </ErrorBoundary>
);
